import { DT_HUD_HOVERED_ASSET_EVENT, DT_HUD_UNHOVERED_ASSET_EVENT } from "../../DtEventTypes";
import { DtEventUtils } from "../../../lmv-compat/DtEventUtils";
import {ConnectivityUtils} from "../../connectivity/ConnectivityUtils";
import { AllFacetsScopes, FacetTypes } from "../../facets/Facets";
import {getLatestClientRect} from "./PositionedUI";

export const kClickThreshold = 2; // Pixels, same as ToolController

// To enable viewer interactions to start from a UI element (ex: pan, orbit)
// Viewer interprets sequential mouse down/up events as clicks, but can be interrupted by a mouse move event.
// When a clickable plugin is registered, we need to add a listener to the document to consume the mouse down events
// if a mouse up event is fired.  Otherwise, relay the mouse down event to the viewer if a mouse move event is fired
export class MouseInteractionsPlugin {
	#boundMouseDown = null;
	#boundMouseUp = null;
	#boundMouseMove = null;
	#boundMouseLeave = null;
	#boundMouseEnter = null;
	#boundMouseExit = null;

	init(ui) {
		const viewer = ui.hud.viewer;
		const eventTarget = ui.eventTarget || ui.innerDom;

		let initialMouseDownEvent = null;

		ui.restyle = () => {
			let style = '';

			for (const plugin of ui.plugins) {
				if (plugin.boundClick) {
					style = 'pointer';
				}
			}

			eventTarget.style.cursor = style;
		}

		this.#boundMouseDown = (event) => {
			viewer.toolController.__clientToCanvasCoords(event);

        	// Keep mouse down event and listen to mousemove to see if dragging starts
        	initialMouseDownEvent = event;
			eventTarget.addEventListener('mousemove', this.#boundMouseMove);
			eventTarget.addEventListener('mouseleave', this.#boundMouseLeave);

			// Potential click, suppress event to prevent selection behind the label
			event.stopPropagation();
		}

		this.#boundMouseUp = (event) => {
			if (initialMouseDownEvent) {
				initialMouseDownEvent = null;
				eventTarget.removeEventListener('mousemove', this.#boundMouseMove);
				eventTarget.removeEventListener('mouseleave', this.#boundMouseLeave);

				for (const plugin of ui.plugins) {
					if (plugin.boundClick?.(event)) break;
				}

				if (event.bubbles) {
					// Forces specific events to bubble up to the viewer, if stopPropagation isn't called.
					if (DtEventUtils.isRightClick(event)) {
						ui.hud.viewer.triggerContextMenu(event);
					}
				}
			} else {
				// No initial mouse down event, so we just need to relay it to the ToolController
				viewer.toolController.__clientToCanvasCoords(event);
				viewer.toolController.mouseup(event);
			}
		}

		this.#boundMouseMove = (event) => {
			if (initialMouseDownEvent) {
				viewer.toolController.__clientToCanvasCoords(event);
				const deltaX = initialMouseDownEvent.canvasX - event.canvasX;
				const deltaY = initialMouseDownEvent.canvasY - event.canvasY;

				if (Math.abs(deltaX) > kClickThreshold || Math.abs(deltaY) > kClickThreshold) {
					// Once dragging starts and the label is not draggable, pass the initial mouseDown event
					// to the ToolController to let other tools handle it.
					viewer.toolController.mousedown(initialMouseDownEvent);

					initialMouseDownEvent = null;
					eventTarget.removeEventListener('mousemove', this.#boundMouseMove);
					eventTarget.removeEventListener('mouseleave', this.#boundMouseLeave);
				}
			}
		}

		this.#boundMouseLeave = (event) => {
			if (initialMouseDownEvent) {
				// Once mouse exits, pass the initial mouseDown event
				// to the ToolController to let other tools handle it.
				viewer.toolController.mousedown(initialMouseDownEvent);

				initialMouseDownEvent = null;
				eventTarget.removeEventListener('mousemove', this.#boundMouseMove);
				eventTarget.removeEventListener('mouseleave', this.#boundMouseLeave);
			}
		}

		this.#boundMouseEnter = (event) => {
			// Ignore mouse enter events when a button is pressed (drag of any kind)
			if (event.buttons !== 0) return;

			for (const plugin of ui.plugins) {
				if (plugin.boundMouseEnter?.(event)) break;
			}

			// Only add "click" listeners on mouse enter to reduce the number of listeners
			eventTarget.addEventListener('mousedown', this.#boundMouseDown);
			eventTarget.addEventListener('mouseup', this.#boundMouseUp);

			// Once the mouse enters, listen for mouse leave to handle the exit event
			eventTarget.addEventListener('mouseleave', this.#boundMouseExit, { once: true });
		}

		// Handles the same event as boundMouseLeave, but is used as complementary handler for mouseenter
		this.#boundMouseExit = (event) => {
			for (const plugin of ui.plugins) {
				if (plugin.boundMouseLeave?.(event)) break;
			}
		}

		eventTarget.addEventListener('mouseenter', this.#boundMouseEnter);

		ui.restyle?.();
	}

	dispose(ui) {
		const eventTarget = ui.eventTarget || ui.innerDom;

		eventTarget.removeEventListener('mouseenter', this.#boundMouseEnter);
		eventTarget.removeEventListener('mousedown', this.#boundMouseDown);
		eventTarget.removeEventListener('mouseup', this.#boundMouseUp);
		eventTarget.removeEventListener('mouseleave', this.#boundMouseLeave);
		eventTarget.removeEventListener('mouseexit', this.#boundMouseExit);

		eventTarget.style.cursor = '';

		ui.restyle = undefined;
	}
}

// An extension of the MouseInteractionsPlugin that allows for nested mouse interactions.
// This is useful for cases where a UI element contains other UI elements that should also be interactive.
export class NestedMouseInteractionsPlugin extends MouseInteractionsPlugin {
	#boundMouseClickNested = null;
	#boundMouseEnterNested = null;
	#boundMouseExitNested = null;

	init(ui) {
		super.init(ui);

		if (!ui.children?.length) return;

		this.#boundMouseClickNested = (event) => {
			for (const child of ui.children) {
				if (!child.eventTargets.includes(event.currentTarget)) continue;

				for (const plugin of child.plugins) {
					if (plugin.boundClick?.(event)) break;
				}
			}

			// Stop propagation to prevent the event from bubbling up to the parent UI element
			event.stopPropagation();
		}

		this.#boundMouseExitNested = (event) => {
			for (const child of ui.children) {
				if (!child.eventTargets.includes(event.currentTarget)) continue;

				for (const plugin of child.plugins) {
					if (plugin.boundMouseLeave?.(event)) break;
				}
			}
		}

		this.#boundMouseEnterNested = (event) => {
			// Ignore mouse enter events when a button is pressed (drag of any kind)
			if (event.buttons !== 0) return;

			for (const child of ui.children) {
				if (!child.eventTargets.includes(event.currentTarget)) continue;

				for (const plugin of child.plugins) {
					if (plugin.boundMouseEnter?.(event)) break;
				}
			}

			// Only add click listener on mouse enter to reduce the number of listeners
			event.currentTarget.addEventListener('click', this.#boundMouseClickNested);

			// Once the mouse enters, listen for mouse leave to handle the exit event
			event.currentTarget.addEventListener('mouseleave', this.#boundMouseExitNested, { once: true });

			// Stop propagation to prevent the event from bubbling up to the parent UI element
			event.stopPropagation();
		}

		for (const child of ui.children) {
			for (const plugin of child.plugins) {
				// Pass the parent UI element to the plugin to allow for nested interactions
				// plugins mainly rely on the parent UI's data
				plugin.init?.(ui, child);
			}

			for (const eventTarget of child.eventTargets) {
				eventTarget.addEventListener('mouseenter', this.#boundMouseEnterNested);
			}
		}
	}

	dispose(ui) {
		super.dispose(ui);

		if (!ui.children?.length) return;

		for (const child of ui.children) {
			if (child.eventTargets?.length) {
				for (const eventTarget of child.eventTargets) {
					eventTarget.removeEventListener('mouseenter', this.#boundMouseEnterNested);
					eventTarget.removeEventListener('mouseleave', this.#boundMouseExitNested);
					eventTarget.removeEventListener('click', this.#boundMouseClickNested);
				}
			}

			if (child.plugins?.length) {
				for (const plugin of child.plugins) {
					plugin.dispose?.(ui, child);
				}
			}
		}
	}
}

export class SelectOnClick {
	init(ui) {
		this.boundClick = (e) => {
			const { dbId, model } = ui.data;
			if (e.shiftKey) ui.hud.viewer.toggleSelect(dbId, model);
			else ui.hud.viewer.select(dbId, model);

			e.stopPropagation();
		}

		ui.restyle?.();
	}

	dispose(ui) {
		this.boundClick = undefined;

		ui.restyle?.();
	}
}

export class SelectFacetOnClick {
	init(ui) {
		const fm = ui.hud.twin.facetsManager;

		this.boundClick = (e) => {
			const { facetType, node } = ui.data;
			const isPlanMode = ui.hud.twin.getFloorPlanManager().isPlanModeActive();

			if (facetType === FacetTypes.locations && node.isRoom) {
				// Location rooms share the `locations` facet/filter with their levels, so a plain
				// clear-then-add would deselect the parent level. Route them through a room-aware select.
				fm.selectLocationRoom(node.id, e.shiftKey, AllFacetsScopes);
			} else {
				// 2D plan mode: one level at a time (ignore shift+click multi-select on level labels).
				const allowMultiSelect = e.shiftKey && !isPlanMode;
				// Legacy levels and rooms are in separate facets (levels / spaces) with separate filters.
				// Clicking a room clears only the spaces filter and adds the room — the levels filter is untouched,
				// so the level stays selected and sibling rooms remain visible (room visibility keys off the levels filter), enabling multi-select
				if (!allowMultiSelect) fm.clearFilter(facetType, true, AllFacetsScopes);
				fm.setVisibilityById(facetType, node.id, true, true, AllFacetsScopes);
				fm.updateFacets(null, false, true, AllFacetsScopes);
			}

			e.preventDefault();
			e.stopPropagation();
		}

		ui.restyle?.();
	}

	dispose(ui) {
		this.boundClick = undefined;

		ui.restyle?.();
	}
}

export class SelectDeferredOnClick {
	init(ui) {
		this.boundClick = (evt) => {
			const viewer = ui.hud.viewer;
			const clusterType = evt.currentTarget.dataset.hoveredType;
			const assets = collectAssets(ui.data, clusterType);

			if (assets.length) {
				ConnectivityUtils.aggregateSelectElements(viewer, assets);
				viewer.fitToView(viewer.getAggregateSelection());
			}

			evt.stopPropagation();
		}

		ui.restyle?.();
	}

	dispose(ui) {
		this.boundClick = undefined;

		ui.restyle?.();
	}
}

export class HighlightFacetOnHover {
	init(ui) {
		const fm = ui.hud.twin.facetsManager;

		this.boundMouseEnter = (e) => {
			fm.facetsEffects.highlightFacet({ id: ui.data.facetType }, ui.data.node);
			e.stopPropagation();
		}

		this.boundMouseLeave = (e) => {
			fm.facetsEffects.clearFacetHighlight();
			e.stopPropagation();
		}
	}

	dispose(ui) {
		this.boundMouseEnter = undefined;
		this.boundMouseLeave = undefined;
	}
}

export class HighlightDeferredOnHover {
	init(ui) {
		const fm = ui.hud.twin.facetsManager;
		let assets = [];

		this.boundMouseEnter = (evt) => {
			const clusterType = evt.currentTarget.dataset.hoveredType;
			assets = collectAssets(ui.data, clusterType);

			for (const metadata of assets) {
				if (metadata.isSpatial) {
					fm.facetsEffects.addSpaceHighlight(metadata);
				} else {
					fm.facetsEffects.addElementHighlight(metadata.model, metadata.dbId);
				}
			}
			evt.stopPropagation();
		}

		this.boundMouseLeave = (evt) => {
			let hasSpatial = false;
			for (const metadata of assets) {
				if (metadata.isSpatial) {
					hasSpatial = true;
				} else {
					fm.facetsEffects.removeElementHighlight(metadata.model, metadata.dbId);
				}
			}

			if (hasSpatial) {
				// Clear space highlights
				fm.facetsEffects.clearFacetHighlight();
			}

			evt.stopPropagation();
		}
	}

	dispose(ui) {
		this.boundMouseEnter = undefined;
		this.boundMouseLeave = undefined;
	}
}

export class ExpandOnHover {
	#collapsed = false;

	init(ui) {
		const boundMouseEnter = () => {
			if (!this.#collapsed) return;
			ui.uncollapse();
			this.#collapsed = false;
		}

		const boundMouseLeave = () => {
			if (this.#collapsed) return;
			ui.collapse();
			this.#collapsed = true;
		}

		ui.setCollapsible = (collapsible) => {
			if (collapsible) {
				// Collapse by default if collapsible
				ui.collapse();
				this.#collapsed = true;
				this.boundMouseEnter = boundMouseEnter;
				this.boundMouseLeave = boundMouseLeave;
			} else {
				// Uncollapse by default if not collapsible
				ui.uncollapse();
				this.#collapsed = false;
				this.boundMouseEnter = undefined;
				this.boundMouseLeave = undefined;
			}
		}
	}

	dispose(ui) {
		this.boundMouseEnter = undefined;
		this.boundMouseLeave = undefined;

		ui.setCollapsible = undefined;
	}
}

export class FocusOnHover {
	init(ui) {
		this.boundMouseEnter = (e) => {
			ui.dom.style.zIndex = 1;
		}

		this.boundMouseLeave = (e) => {
			ui.dom.style.zIndex = 0;
		}
	}

	dispose(ui) {
		this.boundMouseEnter = undefined;
		this.boundMouseLeave = undefined;
		ui.dom.style.zIndex = 'auto';
	}
}

export class DispatchEventOnHover {
	#hovered = false;

	init(ui, child) {
		this.boundMouseEnter = (e) => {
			this.#hovered = true;
			ui.hud.viewer.dispatchEvent({
				type: DT_HUD_HOVERED_ASSET_EVENT,
				ui,
				target: child ? child.dom : ui.dom,
				clusterType: e.currentTarget.dataset.hoveredType,
			});
		}

		this.boundMouseLeave = (e) => {
			this.#hovered = false;
			ui.hud.viewer.dispatchEvent({ type: DT_HUD_UNHOVERED_ASSET_EVENT });
		}
	}

	dispose(ui) {
		if (this.#hovered) {
			ui.hud.viewer.dispatchEvent({ type: DT_HUD_UNHOVERED_ASSET_EVENT });
			this.#hovered = false;
		}
		this.boundMouseEnter = undefined;
		this.boundMouseLeave = undefined;
	}
}

export class CallbackOnEvent {
	constructor(event, callback) {
		this.event = event;
		this.callback = callback;
	}

	init(ui) {
		const eventTarget = ui.eventTarget || ui.innerDom;
		eventTarget.addEventListener(this.event, this.callback);
	}

	dispose(ui) {
		const eventTarget = ui.eventTarget || ui.innerDom;
		eventTarget.removeEventListener(this.event, this.callback);
	}
}

export class UiCollision {
	// debugging collision can be helped with LabelCompositor's wireframe mode.
	init(ui) {
		const target = { ui };
		console.assert(ui.getEstimatedSize, 'Estimated size is required for collision detection');

		ui.getCollisionTarget = () => {
			getLatestClientRect(ui.dimensions ?? ui.getEstimatedSize(), ui.position.cached2D, ui.anchor, target);

			return target;
		};
	}
}

function collectAssets(data, type) {
	if (type === 'assets') {
		return data.deferred;
	}

	const assets = [];
	for (const deferred of data.deferred) {
		if (hasAssetData(deferred, type)) {
			assets.push(deferred);
		}
	}
	return assets;
}

function hasAssetData(asset, type) {
	switch (type) {
		case 'tickets':
			return asset.tickets?.length > 0;
		case 'alerts':
			return asset.alerts?.length > 0;
		case 'streams':
			return !!asset.connState;
		case 'assets':
			return true;
		default:
			return false;
	}
}
